import React, { useState, useEffect, useContext, useRef } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Typography,
  Grid,
  Fade,
  useMediaQuery,
  useTheme,
  Chip,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import {
  Lock,
  CreditCard,
  QrCode2,
  CalendarToday,
  CheckCircle,
  VerifiedUser,
  Shield,
  ArrowForward,
  ArrowBack,
  WhatsApp,
} from "@mui/icons-material";
import PixPayment from "./PixPayment";
import PagamentoCartao from "./PagamentoCartao";
import { UserContext } from "../userContext/UserContext";
import {
  consumePaymentEntrySource,
  registrarSalesEvent,
} from "../../../utils/salesEvents.js";

const EXIT_REASONS = [
  { value: "just_browsing", label: "Só estava conhecendo a oferta" },
  { value: "pay_later", label: "Vou pagar em outro momento" },
  { value: "price", label: "O valor não cabe no meu orçamento agora" },
  { value: "need_more_information", label: "Ainda preciso entender melhor o produto" },
  { value: "payment_method", label: "Não encontrei a forma de pagamento que preciso" },
  { value: "technical_issue", label: "Tive dificuldade ou apareceu um erro" },
];

const Pagamento = ({ userId, onExit }) => {
  const [metodoPagamento, setMetodoPagamento] = useState("pix");
  const [exitSurveyOpen, setExitSurveyOpen] = useState(false);
  const [exitReason, setExitReason] = useState("");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [valorPix, setValorPix] = useState(null); // Valor padrão nulo para sabermos se a requisição já retornou
  const [valorCartaoParcelado2x, setValorCartaoParcelado2x] = useState(null);
  const [totalCartaoParcelado2x, setTotalCartaoParcelado2x] = useState(null);
  const mountedAtRef = useRef(Date.now());
  const paymentStageRef = useRef("method_selected");
  const selectedMethodRef = useRef("pix");
  const autoPixTrackedRef = useRef(false);
  const exitTrackedRef = useRef(false);
  const entrySourceRef = useRef(null);
  if (entrySourceRef.current === null) {
    entrySourceRef.current = consumePaymentEntrySource();
  }

  const { valorPagamento, ofertaPagamento, user } = useContext(UserContext);
  const whatsappNumber = process.env.REACT_APP_WHATSAPP_NUMBER?.replace(/\D/g, "");
  const whatsappHelpLink = whatsappNumber
    ? `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(
        "Oi! Estou na tela de pagamento e ficou uma dúvida."
      )}`
    : "";
  const isTestOffer = Boolean(ofertaPagamento?.isTestOffer);
  const accessDurationLabel = ofertaPagamento?.accessDurationLabel || "12 meses";
  const cardPaymentAllowed = ofertaPagamento?.cardPaymentAllowed === true;

  useEffect(() => {
    if (!userId) return;

    mountedAtRef.current = Date.now();
    paymentStageRef.current = "method_selection";
    selectedMethodRef.current = null;
    exitTrackedRef.current = false;
    if (!autoPixTrackedRef.current) {
      autoPixTrackedRef.current = true;
      registrarSalesEvent(userId, "payment_method_selected", {
        source: "payment_page",
        metadata: {
          method: "pix",
          amount: valorPagamento,
          entry_source: entrySourceRef.current,
          opened_directly: true,
        },
      });
    }

    registrarSalesEvent(userId, "payment_view", {
      source: "payment_page",
      metadata: {
        entry_source: entrySourceRef.current,
        amount: valorPagamento,
        is_test_offer: isTestOffer,
        access_duration: accessDurationLabel,
      },
    });

    const trackExit = (trigger = "component_unmount", reason = null) => {
      if (exitTrackedRef.current || paymentStageRef.current === "completed") return;

      const elapsed = Date.now() - mountedAtRef.current;
      if (elapsed < 2000) return;

      exitTrackedRef.current = true;
      const metadata = {
        trigger,
        reason,
        stage: paymentStageRef.current,
        method: selectedMethodRef.current,
        elapsed_ms: elapsed,
        entry_source: entrySourceRef.current,
      };

      registrarSalesEvent(userId, "payment_checkout_exit", {
        source: "payment_page",
        metadata,
        keepalive: true,
      });

      if (elapsed >= 60000 && paymentStageRef.current !== "charge_created") {
        registrarSalesEvent(userId, "payment_abandoned", {
          source: "payment_page",
          metadata,
          keepalive: true,
        });
      }
    };

    const handlePageHide = () => trackExit("page_hide");
    window.addEventListener("pagehide", handlePageHide);

    return () => {
      window.removeEventListener("pagehide", handlePageHide);
      trackExit("component_unmount");
    };
    // A visita representa uma unica montagem do checkout.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const handlePaymentHelpWhatsapp = () => {
    if (userId) {
      registrarSalesEvent(userId, "payment_help_whatsapp_click", {
        source: "payment_page",
        metadata: { method_selected: metodoPagamento || null },
      });
    }

    if (whatsappHelpLink) {
      window.open(whatsappHelpLink, "_blank", "noopener,noreferrer");
    }
  };

  useEffect(() => {
    const fetchValorPix = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          console.error("Token não encontrado. Faça login novamente.");
          return;
        }

        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/payments/consultar-valor/${userId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const data = await res.json();
        if (res.ok && data?.transaction_amount) {
          setValorPix(data.transaction_amount);
        } else {
          registrarSalesEvent(userId, "payment_value_load_failed", {
            source: "payment_page",
            metadata: {
              method: "pix",
              reason: res.ok ? "missing_amount" : "http_error",
              http_status: res.status,
            },
          });
        }
      } catch (error) {
        console.error("❌ Erro ao consultar valor Pix:", error);
        registrarSalesEvent(userId, "payment_value_load_failed", {
          source: "payment_page",
          metadata: { method: "pix", reason: "network_error" },
        });
      }
    };

    fetchValorPix();
  }, [userId]);

  const [valorCartao, setValorCartao] = useState(null);

  useEffect(() => {
    const fetchValorCartao = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/payments/consultar-valor-cartao/${userId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const data = await res.json();
        if (res.ok && data?.transaction_amount) {
          setValorCartao(data.transaction_amount);
          setValorCartaoParcelado2x(
            data?.installment_options?.two_installments_amount ?? null
          );
          setTotalCartaoParcelado2x(
            data?.installment_options?.two_installments_total ?? null
          );
        } else {
          setValorCartao(null);
          setValorCartaoParcelado2x(null);
          setTotalCartaoParcelado2x(null);
          const cardIntentionallyUnavailable =
            data?.card_payment_allowed === false;

          if (!cardIntentionallyUnavailable) {
            registrarSalesEvent(userId, "payment_value_load_failed", {
              source: "payment_page",
              metadata: {
                method: "card",
                reason: res.ok ? "missing_amount" : "http_error",
                http_status: res.status,
              },
            });
          }
        }
      } catch (error) {
        console.error("❌ Erro ao consultar valor Cartão:", error);
        setValorCartao(null);
        setValorCartaoParcelado2x(null);
        setTotalCartaoParcelado2x(null);
        registrarSalesEvent(userId, "payment_value_load_failed", {
          source: "payment_page",
          metadata: { method: "card", reason: "network_error" },
        });
      }
    };
    fetchValorCartao();
  }, [userId]);

  const handleMetodoPagamento = (metodo) => {
    selectedMethodRef.current = metodo;
    paymentStageRef.current = "method_selected";
    setMetodoPagamento(metodo);
    registrarSalesEvent(userId, "payment_method_selected", {
      source: "payment_page",
      metadata: {
        method: metodo,
        amount: metodo === "pix" ? valorPixExibicao : valorCartao,
        entry_source: entrySourceRef.current,
      },
    });
  };

  const handlePaymentStageChange = (stage) => {
    paymentStageRef.current = stage;
  };

  const handleOpenExitSurvey = () => {
    setExitSurveyOpen(true);
    registrarSalesEvent(userId, "payment_exit_survey_view", {
      source: "payment_page",
      metadata: {
        stage: paymentStageRef.current,
        method: selectedMethodRef.current,
      },
    });
  };

  const finishExit = (reason = null) => {
    const elapsed = Date.now() - mountedAtRef.current;

    if (reason) {
      registrarSalesEvent(userId, "payment_exit_reason", {
        source: "payment_page",
        metadata: {
          reason,
          stage: paymentStageRef.current,
          method: selectedMethodRef.current,
          elapsed_ms: elapsed,
          entry_source: entrySourceRef.current,
        },
      });
    }

    if (!exitTrackedRef.current) {
      exitTrackedRef.current = true;
      registrarSalesEvent(userId, "payment_checkout_exit", {
        source: "payment_page",
        metadata: {
          trigger: "explicit_exit",
          reason,
          stage: paymentStageRef.current,
          method: selectedMethodRef.current,
          elapsed_ms: elapsed,
          entry_source: entrySourceRef.current,
        },
        keepalive: true,
      });
    }

    setExitSurveyOpen(false);
    onExit?.();
  };

  const valorPixExibicao = valorPagamento ?? valorPix;
  const cartaoDisponivel =
    cardPaymentAllowed && typeof valorCartao === "number" && valorCartao > 0;
  const formatCurrency = (value) =>
    typeof value === "number"
      ? value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
      : "Carregando...";
  const formatInstallment = (value) =>
    typeof value === "number"
      ? `2x de ${Number(valorCartaoParcelado2x ?? value / 2).toLocaleString("pt-BR", {
          style: "currency",
          currency: "BRL",
        })}`
      : "2x disponível";
  const valorTotalCartaoExibicao = totalCartaoParcelado2x ?? valorCartao;

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3.5 },
        maxWidth: 920,
        mx: "auto",
        bgcolor: "rgba(8,14,22,0.92)",
        borderRadius: 3,
        boxShadow: "0 16px 45px rgba(0,0,0,0.35)",
        position: "relative",
        overflow: "hidden",
        border: "1px solid rgba(255,255,255,0.09)",
      }}
    >
      <Box sx={{ position: "relative" }}>
        {/* Tela de seleção de método */}
        {metodoPagamento === "" && (
          <Fade in timeout={500}>
            <Box>
              <Stack spacing={2.2} sx={{ mb: 3.2 }}>
                <Chip
                  icon={<VerifiedUser />}
                  label="Checkout protegido"
                  sx={{
                    alignSelf: "flex-start",
                    color: "#CFE9FF",
                    bgcolor: "rgba(73, 149, 255, 0.18)",
                    border: "1px solid rgba(73,149,255,0.35)",
                    fontWeight: 700,
                  }}
                />
                <Typography
                  variant={isMobile ? "h5" : "h4"}
                  sx={{ fontWeight: 800, color: "rgba(255,255,255,0.96)", lineHeight: 1.15 }}
                >
                  Finalize sua assinatura com segurança
                </Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.72)", maxWidth: 640 }}>
                  Escolha a forma de pagamento. A liberação é automática após confirmação.
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
                  <Chip
                    icon={<Shield />}
                    label="Proteção antifraude"
                    sx={{ color: "rgba(255,255,255,0.9)", bgcolor: "rgba(255,255,255,0.06)" }}
                  />
                  <Chip
                    icon={<Lock />}
                    label="Criptografia TLS"
                    sx={{ color: "rgba(255,255,255,0.9)", bgcolor: "rgba(255,255,255,0.06)" }}
                  />
                </Stack>
              </Stack>

              <Grid container spacing={2.2}>
                <Grid item xs={12} md={cartaoDisponivel ? 6 : 12}>
                  <Button
                    fullWidth
                    variant="outlined"
                    disabled={!(Number(valorPixExibicao) > 0)}
                    onClick={() => handleMetodoPagamento("pix")}
                    sx={{
                      p: 2.4,
                      borderRadius: 2.5,
                      borderColor: "rgba(69, 214, 126, 0.55)",
                      background: "linear-gradient(180deg, rgba(27,60,44,0.55) 0%, rgba(20,42,33,0.7) 100%)",
                      color: "rgba(239,250,244,0.98)",
                      textAlign: "left",
                      justifyContent: "flex-start",
                      "&:hover": {
                        borderColor: "#45D67E",
                        bgcolor: "rgba(69,214,126,0.15)",
                      },
                    }}
                  >
                    <Box sx={{ width: "100%" }}>
                      <Typography sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.7, fontWeight: 800 }}>
                        <QrCode2 fontSize="small" />
                        PIX
                      </Typography>
                      <Typography sx={{ fontSize: "1.36rem", fontWeight: 900, lineHeight: 1.1 }}>
                        {formatCurrency(valorPixExibicao)}
                      </Typography>
                      <Typography sx={{ mt: 0.55, color: "rgba(239,250,244,0.82)", fontSize: "0.85rem" }}>
                        confirmação geralmente em segundos
                      </Typography>
                      <Box sx={{ mt: 1.3, display: "flex", alignItems: "center", gap: 0.7 }}>
                        <ArrowForward sx={{ fontSize: 16 }} />
                        <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>Continuar com Pix</Typography>
                      </Box>
                    </Box>
                  </Button>
                </Grid>

                {cartaoDisponivel ? (
                <Grid item xs={12} md={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => handleMetodoPagamento("cartao")}
                    sx={{
                      p: 2.4,
                      borderRadius: 2.5,
                      borderColor: "rgba(133,170,255,0.45)",
                      background: "linear-gradient(180deg, rgba(24,36,66,0.48) 0%, rgba(17,26,49,0.68) 100%)",
                      color: "rgba(241,246,255,0.98)",
                      textAlign: "left",
                      justifyContent: "flex-start",
                      "&:hover": {
                        borderColor: "rgba(133,170,255,0.8)",
                        bgcolor: "rgba(133,170,255,0.12)",
                      },
                    }}
                  >
                    <Box sx={{ width: "100%" }}>
                      <Typography sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.7, fontWeight: 800 }}>
                        <CreditCard fontSize="small" />
                        Cartão de crédito
                      </Typography>
                      <Typography sx={{ fontSize: "1.36rem", fontWeight: 900, lineHeight: 1.1 }}>
                        {formatInstallment(valorCartao)}
                      </Typography>
                      <Typography sx={{ mt: 0.55, color: "rgba(241,246,255,0.82)", fontSize: "0.85rem" }}>
                        total {formatCurrency(valorTotalCartaoExibicao)} no cartão parcelado
                      </Typography>
                      <Box sx={{ mt: 1.3, display: "flex", alignItems: "center", gap: 0.7 }}>
                        <ArrowForward sx={{ fontSize: 16 }} />
                        <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>Continuar com cartão</Typography>
                      </Box>
                    </Box>
                  </Button>
                </Grid>
                ) : null}
              </Grid>

              <Box
                sx={{
                  mt: 2.6,
                  p: 2,
                  borderRadius: 2.5,
                  border: "1px solid rgba(255,255,255,0.1)",
                  bgcolor: "rgba(255,255,255,0.03)",
                }}
              >
                <Typography sx={{ fontWeight: 800, color: "rgba(255,255,255,0.95)", mb: 1.2 }}>
                  {isTestOffer
                    ? `Oferta de teste • ${accessDurationLabel}`
                    : `Assinatura Premium • ${accessDurationLabel}`}
                </Typography>
                <Stack spacing={1}>
                  <Typography sx={{ color: "rgba(255,255,255,0.78)", display: "flex", alignItems: "center", gap: 1 }}>
                    <CreditCard sx={{ fontSize: 16 }} />
                    Pagamento único: <strong>{formatCurrency(valorPixExibicao)}</strong>
                    {!cartaoDisponivel ? " via Pix" : ""}
                  </Typography>
                  <Typography sx={{ color: "rgba(255,255,255,0.78)", display: "flex", alignItems: "center", gap: 1 }}>
                    <CalendarToday sx={{ fontSize: 16 }} />
                    Validade de {accessDurationLabel} após confirmação
                  </Typography>
                </Stack>

                <Divider sx={{ my: 1.5, borderColor: "rgba(255,255,255,0.1)" }} />

                <Stack spacing={0.8}>
                  <Typography sx={{ color: "rgba(255,255,255,0.9)", display: "flex", alignItems: "center", gap: 1 }}>
                    <CheckCircle sx={{ fontSize: 16, color: "#5FDE91" }} />
                    Atualizações mensais incluídas
                  </Typography>
                  <Typography sx={{ color: "rgba(255,255,255,0.9)", display: "flex", alignItems: "center", gap: 1 }}>
                    <CheckCircle sx={{ fontSize: 16, color: "#5FDE91" }} />
                    Vídeos e materiais de implementação
                  </Typography>
                  <Typography sx={{ color: "rgba(255,255,255,0.9)", display: "flex", alignItems: "center", gap: 1 }}>
                    <CheckCircle sx={{ fontSize: 16, color: "#5FDE91" }} />
                    Acesso ao Termômetro Financeiro
                  </Typography>
                </Stack>
              </Box>

              {whatsappHelpLink ? (
                <Button
                  variant="text"
                  size="small"
                  startIcon={<WhatsApp />}
                  onClick={handlePaymentHelpWhatsapp}
                  sx={{
                    display: "flex",
                    mx: "auto",
                    mt: 2,
                    color: "rgba(255,255,255,0.78)",
                    textTransform: "none",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.06)" },
                  }}
                >
                  Ficou com dúvida? Fale no WhatsApp
                </Button>
              ) : null}

              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  textAlign: "center",
                  mt: 2.2,
                  color: "rgba(255,255,255,0.62)",
                  lineHeight: 1.45,
                  px: 1,
                }}
              >
                Seus dados de pagamento são processados em ambiente seguro e não ficam armazenados em nossos servidores.
              </Typography>
            </Box>
          </Fade>
        )}

        {/* Componentes de pagamento */}
        <Box sx={{ position: "relative", mt: 2 }}>
          {metodoPagamento === "pix" && (
            <Fade in timeout={300}>
              <Box>
                {/* Passando o valorPix para o componente PixPayment */}
                <PixPayment
                  valorPagamento={valorPixExibicao}
                  initialBuyer={{
                    fullName: user?.fullName || "",
                    email: user?.email || "",
                  }}
                  onCancel={
                    cartaoDisponivel ? () => handleMetodoPagamento("cartao") : undefined
                  }
                  cancelLabel={cartaoDisponivel ? "Pagar com cartão" : ""}
                  onStageChange={handlePaymentStageChange}
                  accessDurationLabel={accessDurationLabel}
                  userId={userId}
                />
              </Box>
            </Fade>
          )}

          {metodoPagamento === "cartao" && (
            <Fade in timeout={300}>
              <Box>
                <PagamentoCartao
                  valorPagamento={valorCartao}
                  valorParcela2x={valorCartaoParcelado2x}
                  initialBuyer={{
                    fullName: user?.fullName || "",
                    email: user?.email || "",
                    phone: user?.phone || "",
                  }}
                  onCancel={() => handleMetodoPagamento("pix")}
                  onStageChange={handlePaymentStageChange}
                  userId={userId}
                />
              </Box>
            </Fade>
          )}
        </Box>

        {onExit ? (
          <Button
            variant="text"
            size="small"
            startIcon={<ArrowBack />}
            onClick={handleOpenExitSurvey}
            sx={{
              display: "flex",
              mx: "auto",
              mt: 2.2,
              color: "rgba(255,255,255,0.72)",
              textTransform: "none",
            }}
          >
            Voltar ao painel
          </Button>
        ) : null}
      </Box>

      <Dialog
        open={exitSurveyOpen}
        onClose={() => setExitSurveyOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            bgcolor: "#101925",
            color: "#F5F8FC",
            border: "1px solid rgba(255,255,255,0.12)",
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 0.8 }}>
          Antes de sair, pode nos ajudar?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "rgba(245,248,252,0.72)", mb: 1.5 }}>
            Qual é o principal motivo para não continuar agora?
          </Typography>
          <RadioGroup
            value={exitReason}
            onChange={(event) => setExitReason(event.target.value)}
          >
            {EXIT_REASONS.map((reason) => (
              <FormControlLabel
                key={reason.value}
                value={reason.value}
                control={<Radio sx={{ color: "rgba(255,255,255,0.55)" }} />}
                label={reason.label}
                sx={{
                  m: 0,
                  py: 0.35,
                  alignItems: "flex-start",
                  "& .MuiFormControlLabel-label": { pt: 1, lineHeight: 1.3 },
                }}
              />
            ))}
          </RadioGroup>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, flexWrap: "wrap", gap: 1 }}>
          <Button
            onClick={() => setExitSurveyOpen(false)}
            sx={{ color: "#A9CFFF", textTransform: "none", fontWeight: 700 }}
          >
            Continuar no pagamento
          </Button>
          <Button
            onClick={() => finishExit(exitReason || null)}
            variant="contained"
            sx={{ textTransform: "none", fontWeight: 800 }}
          >
            {exitReason ? "Enviar e sair" : "Sair sem responder"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

Pagamento.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onExit: PropTypes.func,
};

export default Pagamento;
